import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  User,
  ListOrdered,
  CreditCard,
  MessageSquare,
  LogOut,
  Save,
  Send,
  Calendar,
  Tag,
  Receipt,
  CheckCircle2,
  Clock,
  AtSign,
  Hash,
  AlertCircle,
  Mail,
} from 'lucide-react';
import { trpc } from '@/providers/trpc';
import { useTelegram } from '@/hooks/useTelegram';

type Tab = 'profile' | 'orders' | 'payments' | 'messages';

export default function Account() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isInTelegram } = useTelegram();
  const [tab, setTab] = useState<Tab>('profile');

  const meQuery = trpc.account.me.useQuery(undefined, { retry: false });
  const summaryQuery = trpc.account.summary.useQuery(undefined, {
    retry: false,
    enabled: !!meQuery.data,
  });

  if (meQuery.isError) {
    // In the browser: send the user to /login (cookie missing or expired).
    // Inside the Mini App: the initData header should authenticate
    // automatically — if it didn't, show a soft error instead of bouncing
    // them to a Login Widget that won't render properly in-app.
    if (!isInTelegram) {
      setTimeout(() => navigate('/login', { replace: true }), 0);
      return null;
    }
    return (
      <div className="min-h-[100dvh] bg-[#0B0E14] text-[#F0F2F5] flex items-center justify-center px-6">
        <div className="glass-card rounded-2xl p-8 max-w-sm text-center">
          <AlertCircle size={32} className="text-[#FFB020] mx-auto mb-4" />
          <h2 className="text-lg font-bold mb-2">
            {t('account.authErrorTitle') ?? t('auth.signIn')}
          </h2>
          <p className="text-[#8A93A7] text-sm mb-5">
            {meQuery.error?.message ?? t('aiError')}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-11 px-5 glass-btn text-[#0B0E14] text-sm font-semibold rounded-xl"
          >
            ← {t('auth.backToHome')}
          </Link>
        </div>
      </div>
    );
  }

  if (meQuery.isLoading) {
    return (
      <div className="min-h-[100dvh] bg-[#0B0E14] flex items-center justify-center">
        <p className="text-[#8A93A7] text-sm">{t('auth.loading')}</p>
      </div>
    );
  }

  const me = meQuery.data;
  if (!me) return null;
  const s = summaryQuery.data;

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    navigate('/login', { replace: true });
  };

  const fullName =
    [me.firstName, me.lastName].filter(Boolean).join(' ') || 'Telegram User';

  return (
    <div className="min-h-[100dvh] bg-[#0B0E14] text-[#F0F2F5] relative">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />

      <div className="relative z-10 max-w-3xl mx-auto px-5 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="text-[#8A93A7] text-xs hover:text-[#F0F2F5]">
            ← {t('auth.backToHome')}
          </Link>
          {!isInTelegram && (
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-[#8A93A7] text-xs hover:text-[#FF4757] transition-colors"
            >
              <LogOut size={12} />
              {t('auth.logout')}
            </button>
          )}
        </div>

        {/* Profile header — photo, name, identifiers */}
        <div className="glass-card rounded-2xl p-6 mb-4">
          <div className="flex items-center gap-4">
            {me.photoUrl ? (
              <img
                src={me.photoUrl}
                alt=""
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(0,212,255,0.2) 0%, rgba(0,212,255,0.05) 100%)',
                  color: '#00D4FF',
                }}
              >
                {fullName[0]}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-[#F0F2F5] text-xl font-bold truncate">
                {fullName}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[12px] text-[#8A93A7]">
                {/* Render identity row by account type — TG users get their
                    @username + numeric id; email users get their email
                    (truncated if long). Never show "Hash local:abc" — it's
                    an internal placeholder, not something users should see. */}
                {me.isEmailUser ? (
                  me.email && (
                    <span className="flex items-center gap-1 min-w-0">
                      <Mail size={11} className="flex-shrink-0" />
                      <span className="truncate">{me.email}</span>
                    </span>
                  )
                ) : (
                  <>
                    {me.telegramUsername && (
                      <span className="flex items-center gap-1">
                        <AtSign size={11} />@{me.telegramUsername}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Hash size={11} />
                      {me.telegramUserId}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Summary mini-stats */}
          {s && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-5 border-t border-[rgba(255,255,255,0.06)]">
              <StatBox
                value={s.ordersTotal}
                label={t('account.summary.orders')}
              />
              <StatBox
                value={s.ordersActive}
                label={t('account.summary.active')}
                color="#FFB020"
              />
              <StatBox
                value={s.ordersCompleted}
                label={t('account.summary.completed')}
                color="#00E5A0"
              />
              <StatBox
                value={`$${s.paidUSD}${s.paidStars ? ` · ${s.paidStars}⭐` : ''}`}
                label={t('account.summary.paid')}
                color="#00D4FF"
              />
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-hide -mx-5 px-5">
          {([
            ['profile', User, t('account.tabs.profile')],
            ['orders', ListOrdered, t('account.tabs.orders')],
            ['payments', CreditCard, t('account.tabs.payments')],
            ['messages', MessageSquare, t('account.tabs.messages')],
          ] as const).map(([id, Icon, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all
                ${tab === id ? 'bg-[#00D4FF] text-[#0B0E14] shadow-md shadow-[rgba(0,212,255,0.25)]' : 'glass-pill text-[#B5BBC9] hover:text-[#F0F2F5]'}`}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {tab === 'profile' && <ProfileTab me={me} />}
        {tab === 'orders' && <OrdersTab onJumpToPayments={() => setTab('payments')} />}
        {tab === 'payments' && <PaymentsTab onJumpToOrders={() => setTab('orders')} />}
        {tab === 'messages' && <MessagesTab />}
      </div>
    </div>
  );
}

// ─── PROFILE ────────────────────────────────────────────────────────────────

function ProfileTab({
  me,
}: {
  me: {
    firstName: string | null;
    lastName: string | null;
    telegramUsername: string | null;
    telegramUserId: string;
    email: string | null;
    phone: string | null;
    notes: string | null;
    language: string;
    createdAt: Date | string;
    lastLoginAt: Date | string | null;
    isEmailUser: boolean;
  };
}) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const [email, setEmail] = useState(me.email ?? '');
  const [phone, setPhone] = useState(me.phone ?? '');
  const [notes, setNotes] = useState(me.notes ?? '');
  const [saved, setSaved] = useState(false);

  const update = trpc.account.updateContact.useMutation({
    onSuccess: () => {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      void utils.account.me.invalidate();
    },
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate({
      email: email || undefined,
      phone: phone || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <div className="space-y-4">
      {/* Read-only account identity. The block is rendered differently for
          Telegram-authenticated users and email-authenticated users: TG users
          see their Telegram fields (username, numeric id), email users see a
          cleaner "Account info" block with just the email + registration date.
          Never expose the synthetic "local:..." tg id to email users. */}
      <div className="glass-card rounded-2xl p-6 space-y-3">
        <h2 className="text-[#F0F2F5] text-base font-semibold mb-2">
          {t(
            me.isEmailUser
              ? 'account.profile.accountInfo'
              : 'account.profile.telegramData'
          )}
        </h2>
        <ReadOnlyRow
          label={t('account.profile.firstName')}
          value={me.firstName ?? '—'}
        />
        {me.isEmailUser ? (
          <ReadOnlyRow
            label={t('account.profile.email') ?? 'Email'}
            value={me.email ?? '—'}
            mono
          />
        ) : (
          <>
            <ReadOnlyRow
              label={t('account.profile.lastName')}
              value={me.lastName ?? '—'}
            />
            <ReadOnlyRow
              label={t('account.profile.username')}
              value={me.telegramUsername ? `@${me.telegramUsername}` : '—'}
            />
            <ReadOnlyRow
              label={t('account.profile.telegramId')}
              value={me.telegramUserId}
              mono
            />
          </>
        )}
        <ReadOnlyRow
          label={t('account.profile.registered')}
          value={fmtDate(me.createdAt)}
        />
        <ReadOnlyRow
          label={t('account.profile.lastLogin')}
          value={me.lastLoginAt ? fmtDate(me.lastLoginAt) : '—'}
        />
      </div>

      {/* Editable contact details */}
      <form onSubmit={handleSave} className="glass-card rounded-2xl p-6 space-y-4">
        <h2 className="text-[#F0F2F5] text-base font-semibold mb-1">
          {t('account.profile.title')}
        </h2>
        <p className="text-[#8A93A7] text-[12px] -mt-1 mb-2">
          {t('account.profile.subtitle')}
        </p>

        <FieldLabel label={t('account.profile.email')}>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="you@example.com"
            // Email is the login for email-auth users — locking it here so it
            // can't be accidentally changed in the contact-edit flow. Changing
            // the login should be a separate, deliberate action.
            disabled={me.isEmailUser}
            className={`w-full h-11 glass-input rounded-xl px-4 text-[#F0F2F5] text-sm placeholder:text-[#8A93A7]/50 ${
              me.isEmailUser ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          />
        </FieldLabel>

        <FieldLabel label={t('account.profile.phone')}>
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="+7 999 123-45-67"
            className="w-full h-11 glass-input rounded-xl px-4 text-[#F0F2F5] text-sm placeholder:text-[#8A93A7]/50"
          />
        </FieldLabel>

        <FieldLabel label={t('account.profile.notes')}>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder={t('account.profile.notesPlaceholder')}
            className="w-full glass-input rounded-xl px-4 py-3 text-[#F0F2F5] text-sm placeholder:text-[#8A93A7]/50 resize-none"
          />
        </FieldLabel>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={update.isPending}
            className="flex items-center gap-2 h-11 glass-btn text-[#0B0E14] font-bold text-sm rounded-xl px-6 disabled:opacity-50"
          >
            <Save size={14} />
            {update.isPending ? t('account.profile.saving') : t('account.profile.save')}
          </button>
          {saved && (
            <span className="text-[#00E5A0] text-xs flex items-center gap-1">
              <CheckCircle2 size={12} /> {t('account.profile.saved')}
            </span>
          )}
          {update.isError && (
            <span className="text-[#FF4757] text-xs">
              {update.error?.message ?? 'Error'}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

// ─── ORDERS ─────────────────────────────────────────────────────────────────

function OrdersTab({ onJumpToPayments }: { onJumpToPayments: () => void }) {
  const { t } = useTranslation();
  const ordersQuery = trpc.account.myOrders.useQuery({ limit: 50 });
  const paymentsQuery = trpc.account.myPayments.useQuery({ limit: 100 });

  if (ordersQuery.isLoading) return <EmptyCard text={t('account.loading')} />;
  const list = ordersQuery.data ?? [];
  if (list.length === 0) return <EmptyCard text={t('account.orders.empty')} />;

  // Group payments by orderId for quick lookup
  const paymentsByOrder = new Map<number, typeof paymentsQuery.data extends infer P ? (P extends Array<infer R> ? R : never) : never[]>(
  );
  for (const p of paymentsQuery.data ?? []) {
    const arr = paymentsByOrder.get(p.orderId);
    if (arr) (arr as unknown as typeof p[]).push(p);
    else paymentsByOrder.set(p.orderId, [p] as never);
  }

  return (
    <div className="space-y-3">
      {list.map(o => {
        const myPayments =
          (paymentsByOrder.get(Number(o.id)) as unknown as Array<{
            id: number;
            amount: number;
            currency: string;
            method: string;
            status: string;
            createdAt: Date | string;
          }>) ?? [];
        const completed = myPayments.find(p => p.status === 'completed');
        return (
          <div key={o.id} className="glass-card rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <p className="text-[#F0F2F5] text-base font-semibold">
                  {t('account.orders.orderNo', { id: o.id })} · {o.serviceName}
                </p>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-[#8A93A7]">
                  <span className="flex items-center gap-1">
                    <Calendar size={11} />
                    {fmtDate(o.createdAt)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Tag size={11} />
                    {o.serviceId}
                  </span>
                </div>
              </div>
              <StatusPill status={o.status} />
            </div>

            {o.description && (
              <div className="mt-3 mb-3 p-3 rounded-xl bg-[rgba(0,0,0,0.25)] border border-[rgba(255,255,255,0.04)]">
                <p className="text-[#B5BBC9] text-[12.5px] leading-relaxed whitespace-pre-wrap">
                  {o.description}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-[rgba(255,255,255,0.06)]">
              <MicroField
                label={t('account.orders.price')}
                value={`$${o.price}`}
                accent
              />
              {o.budget && (
                <MicroField
                  label={t('account.orders.budget')}
                  value={o.budget}
                />
              )}
              <MicroField
                label={t('account.orders.paymentMethod')}
                value={o.paymentMethod === 'pending'
                  ? t('account.orders.paymentPending')
                  : o.paymentMethod}
              />
              <MicroField
                label={t('account.orders.payments')}
                value={
                  completed
                    ? `✅ ${completed.amount} ${completed.currency}`
                    : myPayments.length > 0
                      ? `${myPayments.length} pending`
                      : '—'
                }
              />
            </div>

            {myPayments.length > 0 && (
              <button
                onClick={onJumpToPayments}
                className="mt-3 text-[11px] text-[#00D4FF] hover:underline"
              >
                {t('account.orders.viewPayments')} →
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── PAYMENTS ───────────────────────────────────────────────────────────────

function PaymentsTab({ onJumpToOrders }: { onJumpToOrders: () => void }) {
  const { t } = useTranslation();
  const paymentsQuery = trpc.account.myPayments.useQuery({ limit: 50 });

  if (paymentsQuery.isLoading) return <EmptyCard text={t('account.loading')} />;
  const list = paymentsQuery.data ?? [];
  if (list.length === 0) return <EmptyCard text={t('account.payments.empty')} />;

  return (
    <div className="space-y-3">
      {list.map(p => (
        <div key={p.id} className="glass-card rounded-2xl p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[#F0F2F5] text-base font-semibold truncate">
                {p.serviceName}
              </p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-[#8A93A7]">
                <span className="flex items-center gap-1">
                  <Receipt size={11} />
                  {t('account.payments.orderRef', { id: p.orderId })}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={11} />
                  {fmtDate(p.createdAt)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={11} />
                  {p.method}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[#F0F2F5] font-bold text-base">
                {p.amount} {p.currency}
              </p>
              <StatusPill status={p.status} />
            </div>
          </div>
          {p.transactionId && (
            <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.06)]">
              <p className="text-[10px] text-[#8A93A7] uppercase tracking-wider mb-0.5">
                {t('account.payments.txn')}
              </p>
              <p className="text-[11px] text-[#B5BBC9] font-mono break-all">
                {p.transactionId}
              </p>
            </div>
          )}
          <button
            onClick={onJumpToOrders}
            className="mt-3 text-[11px] text-[#00D4FF] hover:underline"
          >
            {t('account.payments.viewOrder')} →
          </button>
        </div>
      ))}
    </div>
  );
}

// ─── MESSAGES ───────────────────────────────────────────────────────────────

function MessagesTab() {
  const { t } = useTranslation();
  const messagesQuery = trpc.account.myMessages.useQuery({ limit: 100 });
  if (messagesQuery.isLoading) return <EmptyCard text={t('account.loading')} />;
  const list = (messagesQuery.data ?? []).filter(m => m.role !== 'system');
  if (list.length === 0)
    return <EmptyCard text={t('account.messages.empty')} />;

  return (
    <div className="space-y-2">
      {list.map(m => (
        <div
          key={m.id}
          className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm
              ${
                m.role === 'user'
                  ? 'bg-[#00D4FF] text-[#0B0E14] rounded-br-md'
                  : 'glass-card text-[#F0F2F5] rounded-bl-md'
              }`}
          >
            <p className="whitespace-pre-wrap break-words">{m.content}</p>
            <p
              className={`text-[10px] mt-1 ${m.role === 'user' ? 'text-[#0B0E14]/70' : 'text-[#8A93A7]'}`}
            >
              {fmtDate(m.createdAt)}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── BUILDING BLOCKS ────────────────────────────────────────────────────────

function StatBox({
  value,
  label,
  color = '#F0F2F5',
}: {
  value: number | string;
  label: string;
  color?: string;
}) {
  return (
    <div className="text-center">
      <p className="text-lg font-extrabold" style={{ color }}>
        {value}
      </p>
      <p className="text-[10px] uppercase tracking-wider text-[#8A93A7] mt-0.5">
        {label}
      </p>
    </div>
  );
}

function ReadOnlyRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3 text-[13px]">
      <span className="text-[#8A93A7]">{label}</span>
      <span
        className={`text-[#F0F2F5] text-right ${mono ? 'font-mono text-[12px]' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

function MicroField({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-[#8A93A7] mb-0.5">
        {label}
      </p>
      <p
        className={`text-[13px] font-semibold truncate ${accent ? 'text-[#00D4FF]' : 'text-[#F0F2F5]'}`}
      >
        {value}
      </p>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const colors: Record<string, { bg: string; fg: string }> = {
    new: { bg: 'rgba(0,212,255,0.12)', fg: '#00D4FF' },
    contacted: { bg: 'rgba(0,212,255,0.12)', fg: '#00D4FF' },
    in_progress: { bg: 'rgba(255,176,32,0.12)', fg: '#FFB020' },
    completed: { bg: 'rgba(0,229,160,0.12)', fg: '#00E5A0' },
    cancelled: { bg: 'rgba(138,147,167,0.12)', fg: '#8A93A7' },
    pending: { bg: 'rgba(255,176,32,0.12)', fg: '#FFB020' },
    processing: { bg: 'rgba(0,212,255,0.12)', fg: '#00D4FF' },
    failed: { bg: 'rgba(255,71,87,0.12)', fg: '#FF4757' },
    refunded: { bg: 'rgba(138,147,167,0.12)', fg: '#8A93A7' },
  };
  const c = colors[status] ?? colors.new;
  return (
    <span
      className="text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
      style={{ background: c.bg, color: c.fg }}
    >
      {status}
    </span>
  );
}

function EmptyCard({ text }: { text: string }) {
  const { t } = useTranslation();
  return (
    <div className="glass-card rounded-2xl p-10 text-center">
      <Send size={28} className="text-[#8A93A7] mx-auto mb-3 opacity-40" />
      <p className="text-[#B5BBC9] text-sm mb-4">{text}</p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 px-5 py-2 rounded-full glass-btn text-[#0B0E14] text-sm font-semibold"
      >
        {t('account.goToCatalog')}
      </Link>
    </div>
  );
}

function FieldLabel({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-[#8A93A7] text-[11px] font-medium uppercase tracking-wider mb-1.5 block">
        {label}
      </label>
      {children}
    </div>
  );
}

function fmtDate(d: Date | string): string {
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
